import { useEffect, useRef, useState } from 'react'

import Flex from '@react-css/flex'
import { Redirect, Route, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { BuildingProvider } from '../../../contexts/buildingContext'
import { useUserContext } from '../../../contexts/userContext'
import { PATH_STRINGS } from '../../../hooks/useGeneratedPaths'
import { media } from '../../../styles/breakpoints'
import { ViewSelector } from '../../common/ViewSelector/ViewSelector'

import { ChangePassword } from './ChangePassword'
import { InspectionTools } from './InspectionTools'
import { Integrations } from './Integrations'
import { ScheduleImportPage } from './schedule_import/ScheduleImportPage'
import { UserInformation } from './UserInformation'
import { UserSettingsOptions } from './UserSettingsOptions'

const TOOLS_UNLOCK_SESSION_KEY = 'didge_tools_unlocked'

export const UserProfile = () => {
  const [settingsMenuExpanded, setSettingsMenuExpanded] =
    useState<boolean>(true)
  const escapePressCountRef = useRef(0)
  const { pathname } = useLocation()
  const { state: userState } = useUserContext()
  const isSuperUser = userState.user_type === 'super'
  const [toolsUnlocked, setToolsUnlocked] = useState<boolean>(() => {
    return sessionStorage.getItem(TOOLS_UNLOCK_SESSION_KEY) === 'true'
  })

  useEffect(() => {
    if (!isSuperUser) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }

      escapePressCountRef.current += 1
      if (escapePressCountRef.current >= 5) {
        setToolsUnlocked(true)
        sessionStorage.setItem(TOOLS_UNLOCK_SESSION_KEY, 'true')
        escapePressCountRef.current = 0
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isSuperUser])

  const canAccessTools = isSuperUser && toolsUnlocked
  const onToolsPath = pathname === PATH_STRINGS.userProfileTools

  return (
    <ProfileLayout>
      <ViewSelector
        title="Settings"
        initialViewSelectorExpanded
        viewingExpanded={settingsMenuExpanded}
        setViewingExpanded={setSettingsMenuExpanded}
      >
        <BuildingProvider>
          <UserSettingsOptions showToolsOption={canAccessTools} />
        </BuildingProvider>
      </ViewSelector>

      <ProfileContent>
        {onToolsPath && !canAccessTools ? (
          <Redirect to={PATH_STRINGS.userProfile} />
        ) : null}
        <Route exact path={PATH_STRINGS.userProfile}>
          <UserInformation />
        </Route>
        <Route exact path={PATH_STRINGS.userProfilePassword}>
          <ChangePassword />
        </Route>
        <Route exact path={PATH_STRINGS.userProfileNotificationPreferences}>
          <></>
        </Route>
        <Route exact path={PATH_STRINGS.userProfileIntegrations}>
          <Integrations />
        </Route>
        <Route exact path={PATH_STRINGS.userProfileScheduleImport}>
          <ScheduleImportPage />
        </Route>
        {canAccessTools ? (
          <Route exact path={PATH_STRINGS.userProfileTools}>
            <InspectionTools />
          </Route>
        ) : null}
      </ProfileContent>
    </ProfileLayout>
  )
}

const ProfileLayout = styled(Flex)`
  @media ${media.belowTablet} {
    flex-direction: column;
  }
`

const ProfileContent = styled.div`
  margin: 20px 0 0 120px;
  flex: 1;
  min-width: 0;

  @media ${media.belowTablet} {
    margin: 0;
    padding: 0 0 24px;
  }
`
